import { SectionLayout } from "@/components/layout/SectionLayout";
import { eveningEvents } from "../data/themes-events";
import { useState } from "react";
import Image from "next/image";
import Link from "next/link";

export const EveningEvents = () => {
  const [selectedSalon, setSelectedSalon] = useState<string>("all");
  const [selectedMonth, setSelectedMonth] = useState<string>("all");
  const uniqueSalons = [
    "all",
    ...Array.from(new Set(eveningEvents.map((event) => event.salon))),
  ];

  // Générer la liste des mois présents dans les events
  const months = Array.from(
    new Set(
      eveningEvents.map((event) => {
        const date = new Date(event.startDateTime);
        return `${date.getFullYear()}-${(date.getMonth() + 1)
          .toString()
          .padStart(2, "0")}`;
      }),
    ),
  );
  const monthLabels: Record<string, string> = {};
  months.forEach((monthStr) => {
    const [year, month] = monthStr.split("-");
    const date = new Date(Number(year), Number(month) - 1);
    monthLabels[monthStr] = date.toLocaleString("fr-FR", {
      month: "long",
      year: "numeric",
    });
  });
  const uniqueMonths = ["all", ...months];

  // Filtrage et tri
  let filteredEvents = eveningEvents;

  // Filtrer les événements passés
  const now = new Date();
  filteredEvents = filteredEvents.filter((event) => {
    const eventDate = new Date(event.startDateTime);
    return eventDate > now;
  });

  if (selectedSalon !== "all") {
    filteredEvents = filteredEvents.filter(
      (event) => event.salon === selectedSalon,
    );
  }
  if (selectedMonth !== "all") {
    filteredEvents = filteredEvents.filter((event) => {
      const date = new Date(event.startDateTime);
      const monthStr = `${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}`;
      return monthStr === selectedMonth;
    });
  }
  filteredEvents = filteredEvents.sort(
    (a, b) =>
      new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime(),
  );

  // Génération de suggestions alternatives si aucun événement ne correspond
  const generateAlternatives = () => {
    const alternatives: Array<{
      type: "salon" | "month";
      salon?: string;
      month?: string;
      months?: string[];
      salons: string[];
    }> = [];

    // Suggestions par salon (même salon, autres mois)
    if (selectedSalon !== "all") {
      const salonEvents = eveningEvents.filter(
        (event) => event.salon === selectedSalon,
      );
      const availableMonths = Array.from(
        new Set(
          salonEvents.map((event) => {
            const date = new Date(event.startDateTime);
            return `${date.getFullYear()}-${(date.getMonth() + 1)
              .toString()
              .padStart(2, "0")}`;
          }),
        ),
      );

      if (availableMonths.length > 0) {
        alternatives.push({
          type: "salon",
          salon: selectedSalon,
          months: availableMonths.map((month) => monthLabels[month]),
          salons: [],
        });
      }
    }

    // Suggestions par mois (même mois, autres salons)
    if (selectedMonth !== "all") {
      const monthEvents = eveningEvents.filter((event) => {
        const date = new Date(event.startDateTime);
        const monthStr = `${date.getFullYear()}-${(date.getMonth() + 1)
          .toString()
          .padStart(2, "0")}`;
        return monthStr === selectedMonth;
      });
      const availableSalons = Array.from(
        new Set(monthEvents.map((event) => event.salon)),
      );

      if (availableSalons.length > 0) {
        alternatives.push({
          type: "month",
          month: monthLabels[selectedMonth],
          salons: availableSalons,
        });
      }
    }

    return alternatives;
  };

  const alternatives =
    filteredEvents.length === 0 ? generateAlternatives() : [];

  return (
    <SectionLayout id="soirees-a-themes">
      <div className="flex w-full flex-col items-start space-y-12">
        <h2 className="font-cormorant text-4xl uppercase tracking-wide text-primary ph:text-5xl">
          Nos évènements à thèmes
        </h2>

        <div className="flex w-full flex-wrap gap-4">
          <div className="relative">
            <select
              value={selectedSalon}
              onChange={(e) => setSelectedSalon(e.target.value)}
              className="appearance-none rounded-none border border-primary bg-transparent py-2 pl-6 pr-12 text-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {uniqueSalons.map((salon) => (
                <option key={salon} value={salon}>
                  {salon === "all" ? "Tous les salons" : salon}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-primary">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                <path
                  d="M7 10l5 5 5-5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>
          <div className="relative">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="appearance-none rounded-none border border-primary bg-transparent py-2 pl-6 pr-12 text-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {uniqueMonths.map((month) => (
                <option key={month} value={month}>
                  {month === "all" ? "Tous les mois" : monthLabels[month]}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-primary">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                <path
                  d="M7 10l5 5 5-5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>
        </div>

        {/* Message informatif si aucun événement ne correspond */}
        {filteredEvents.length === 0 ? (
          <div className="mx-auto w-full max-w-2xl rounded-lg border border-primary/20 bg-gray-50 p-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <svg
                className="h-8 w-8 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                />
                <line
                  x1="12"
                  y1="8"
                  x2="12"
                  y2="12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <circle cx="12" cy="16" r="1" fill="currentColor" />
              </svg>
            </div>
            <h3 className="mb-2 font-cormorant text-2xl text-primary">
              Aucun événement trouvé
            </h3>
            <p className="mb-6 text-gray-600">
              Aucune soirée ne correspond à vos critères de recherche actuels.
            </p>

            {alternatives.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-700">
                  Voici quelques alternatives :
                </h4>
                <div className="space-y-3">
                  {alternatives.map((alt, index) => (
                    <div
                      key={index}
                      className="rounded-md border border-primary/10 bg-white p-4 text-left"
                    >
                      {alt.type === "salon" && (
                        <div>
                          <p className="mb-2 font-medium text-primary">
                            Autres soirées au salon {alt.salon}
                          </p>
                          <p className="text-sm text-gray-600">
                            Disponibles en : {alt.months?.join(", ")}
                          </p>
                          <button
                            onClick={() => setSelectedMonth("all")}
                            className="mt-2 text-sm text-primary hover:underline"
                          >
                            → Voir toutes les soirées de {alt.salon}
                          </button>
                        </div>
                      )}
                      {alt.type === "month" && (
                        <div>
                          <p className="mb-2 font-medium text-primary">
                            Autres soirées en {alt.month}
                          </p>
                          <p className="text-sm text-gray-600">
                            Disponibles dans les salons :{" "}
                            {alt.salons.join(", ")}
                          </p>
                          <button
                            onClick={() => setSelectedSalon("all")}
                            className="mt-2 text-sm text-primary hover:underline"
                          >
                            → Voir toutes les soirées de {alt.month}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => {
                    setSelectedSalon("all");
                    setSelectedMonth("all");
                  }}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-white transition-colors hover:bg-primary/90"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Réinitialiser les filtres
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Grille des événements */
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {filteredEvents.map((event) => (
              <div
                key={event.id}
                className="relative flex min-h-[480px] flex-col overflow-hidden border border-primary/20 bg-white transition-all duration-300 hover:border-primary"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <Image
                    src={event.image}
                    alt={event.title}
                    fill
                    className="object-cover transition-transform duration-300"
                  />
                </div>
                <div className="flex flex-1 flex-col space-y-4 p-6">
                  <h3 className="font-cormorant text-2xl text-primary">
                    {event.title}
                  </h3>
                  <p className="flex-1 whitespace-pre-line text-sm text-gray-600">
                    {event.description}
                  </p>
                  <div className="mt-auto">
                    <div className="flex items-center justify-between border-t border-primary/20 pt-4">
                      {event.price && (
                        <span className="font-medium text-primary">
                          {event.price}€
                        </span>
                      )}
                      <span className="text-sm text-gray-500">
                        {event.salon}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500">
                      <p>
                        {new Date(event.startDateTime).toLocaleDateString(
                          "fr-FR",
                          {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          },
                        )}
                      </p>
                      <p>
                        {new Date(event.startDateTime).toLocaleTimeString(
                          "fr-FR",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}{" "}
                        -{" "}
                        {new Date(event.endDateTime).toLocaleTimeString(
                          "fr-FR",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </p>
                    </div>
                    <div className="mt-4">
                      {event.isComplete ? (
                        <span className="inline-block border border-primary/20 px-3 py-1 text-sm font-medium text-primary">
                          Complet
                        </span>
                      ) : event.isComingSoon ? (
                        <span className="inline-block border border-primary/20 px-3 py-1 text-sm font-medium text-primary">
                          Inscription prochainement
                        </span>
                      ) : event.whatsappLink ? (
                        <Link
                          href={event.whatsappLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="whatsapp-button group relative inline-block pb-[2px] text-sm sm:text-base"
                        >
                          <span>S&apos;inscrire via WhatsApp</span>
                          <div className="absolute bottom-0 left-0 h-[1px] w-full bg-gray-300">
                            <div className="h-full w-0 bg-gold transition-all duration-300 ease-out group-hover:w-full"></div>
                          </div>
                        </Link>
                      ) : (
                        <div className="h-6"></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </SectionLayout>
  );
};
